import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { createPublicClient } from "../api/client";
import { SessionResultsDto } from "../types";
import { isLive } from "../utils/sessionStatus";
import ResultsBody from "../components/ResultsBody";

const PublicResultsPage: React.FC = () => {
  const { token = "" } = useParams();
  const [results, setResults] = useState<SessionResultsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async () => {
    const { data } = await createPublicClient().get<SessionResultsDto>(`/api/public/results/${token}`);
    setResults(data);
  }, [token]);

  useEffect(() => {
    load()
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [load]);

  useEffect(() => {
    if (!results || !isLive(results.state)) return;
    const id = setInterval(() => load().catch(() => {}), 4000);
    return () => clearInterval(id);
  }, [results?.state, load]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary-500" />
      </div>
    );
  }

  if (notFound || !results) {
    return (
      <div className="mx-auto mt-16 max-w-md text-center">
        <div className="text-5xl">🔒</div>
        <h1 className="mt-4 font-display text-2xl font-semibold text-ink">Results aren't available</h1>
        <p className="mt-2 text-ink/65">This link may have been turned off or doesn't exist.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl">
      <div className="rounded-4xl bg-sunset px-6 py-8 text-center shadow-warm ring-1 ring-primary-100">
        <p className="text-xs font-semibold uppercase tracking-wider text-primary-700">{results.familyName}</p>
        <h1 className="mt-1 font-display text-3xl font-semibold text-ink">What we're doing</h1>
        <p className="mt-1 text-sm text-ink/55">Live results · read-only</p>
      </div>
      <div className="mt-6">
        <ResultsBody results={results} />
      </div>
    </div>
  );
};

export default PublicResultsPage;
